import { supabase } from '../lib/supabase';

export const versionService = {
  async getAll(projectId) {
    const { data, error } = await supabase?.from('project_versions')?.select('*')?.eq('project_id', projectId)?.order('version_number', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      projectId: row?.project_id,
      versionNumber: row?.version_number,
      versionName: row?.version_name,
      description: row?.description,
      snapshotData: row?.snapshot_data,
      fileCount: row?.file_count,
      totalSize: row?.total_size,
      createdBy: row?.created_by,
      createdAt: row?.created_at,
      isCurrent: row?.is_current
    })) || [];
  },

  async getById(versionId) {
    const { data, error } = await supabase?.from('project_versions')?.select('*')?.eq('id', versionId)?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      projectId: data?.project_id,
      versionNumber: data?.version_number,
      versionName: data?.version_name,
      description: data?.description,
      snapshotData: data?.snapshot_data,
      fileCount: data?.file_count,
      totalSize: data?.total_size,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      isCurrent: data?.is_current
    };
  },

  async getCurrentVersion(projectId) {
    const { data, error } = await supabase?.from('project_versions')?.select('*')?.eq('project_id', projectId)?.eq('is_current', true)?.single();
    
    if (error) {
      if (error?.code === 'PGRST116') return null;
      throw error;
    }
    
    return {
      id: data?.id,
      projectId: data?.project_id,
      versionNumber: data?.version_number,
      versionName: data?.version_name,
      description: data?.description,
      snapshotData: data?.snapshot_data,
      fileCount: data?.file_count,
      totalSize: data?.total_size,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      isCurrent: data?.is_current
    };
  },

  async create(version) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: nextVersionData } = await supabase?.rpc('get_next_version_number', { p_project_id: version?.projectId });
    const versionNumber = nextVersionData || 1;

    const { data, error } = await supabase?.from('project_versions')?.insert({
        project_id: version?.projectId,
        version_number: versionNumber,
        version_name: version?.versionName,
        description: version?.description,
        snapshot_data: version?.snapshotData,
        file_count: version?.fileCount,
        total_size: version?.totalSize,
        created_by: user?.id,
        is_current: version?.isCurrent || false
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      projectId: data?.project_id,
      versionNumber: data?.version_number,
      versionName: data?.version_name,
      description: data?.description,
      snapshotData: data?.snapshot_data,
      fileCount: data?.file_count,
      totalSize: data?.total_size,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      isCurrent: data?.is_current
    };
  },

  async restore(versionId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const version = await this.getById(versionId);
    if (!version) throw new Error('Version not found');

    const { data, error } = await supabase?.from('project_versions')?.update({ is_current: true })?.eq('id', versionId)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      projectId: data?.project_id,
      versionNumber: data?.version_number,
      versionName: data?.version_name,
      description: data?.description,
      snapshotData: data?.snapshot_data,
      fileCount: data?.file_count,
      totalSize: data?.total_size,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      isCurrent: data?.is_current
    };
  },

  async delete(versionId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const { error } = await supabase?.from('project_versions')?.delete()?.eq('id', versionId);
    
    if (error) throw error;
  },

  async compare(versionId1, versionId2) {
    const [version1, version2] = await Promise.all([
      this.getById(versionId1),
      this.getById(versionId2)
    ]);

    return {
      version1,
      version2,
      differences: {
        fileCountDiff: version2?.fileCount - version1?.fileCount,
        sizeDiff: this.calculateSizeDiff(version1?.totalSize, version2?.totalSize),
        featuresDiff: this.compareFeatures(version1?.snapshotData, version2?.snapshotData),
        techStackDiff: this.compareTechStack(version1?.snapshotData, version2?.snapshotData)
      }
    };
  },

  calculateSizeDiff(size1, size2) {
    const parseSize = (sizeStr) => {
      if (!sizeStr) return 0;
      const match = sizeStr?.match(/([\d.]+)\s*(KB|MB|GB)/i);
      if (!match) return 0;
      const value = parseFloat(match?.[1]);
      const unit = match?.[2]?.toUpperCase();
      if (unit === 'KB') return value / 1024;
      if (unit === 'GB') return value * 1024;
      return value;
    };

    const mb1 = parseSize(size1);
    const mb2 = parseSize(size2);
    const diff = mb2 - mb1;

    if (Math.abs(diff) < 0.1) return '0 MB';
    return `${diff > 0 ? '+' : ''}${diff?.toFixed(1)} MB`;
  },

  compareFeatures(snapshot1, snapshot2) {
    const features1 = snapshot1?.features || [];
    const features2 = snapshot2?.features || [];

    const added = features2?.filter(f => !features1?.includes(f));
    const removed = features1?.filter(f => !features2?.includes(f));

    return { added, removed };
  },

  compareTechStack(snapshot1, snapshot2) {
    const tech1 = snapshot1?.tech_stack || [];
    const tech2 = snapshot2?.tech_stack || [];

    const added = tech2?.filter(t => !tech1?.includes(t));
    const removed = tech1?.filter(t => !tech2?.includes(t));

    return { added, removed };
  }
};

export default versionService;